import React, { useMemo, useState } from "react";
import { Card, Tag, Table, Button, Select, Space, Tooltip, Modal, Popconfirm } from "antd";
import { DatabaseOutlined, LockOutlined, EditOutlined, DeleteOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { MemberProfile } from "../../../../model/members/MemberProfile";
import { MemberMetadataTypeDescriptor } from "../../../../model/members/MemberMetadataTypeDescriptor";
import { tokens } from "../../../../core/theme/tokens";
import {
    EditorValue,
    MetadataValueEditor,
    isValidValue,
    parseStoredValue,
    serializeValue,
    summariseStoredValue,
} from "./metadataValue";

interface MetadataCardProps {
    member: MemberProfile;
    metadataTypes: MemberMetadataTypeDescriptor[];
    // Persists a single metadata entry; a null value clears it. Returns a promise
    // so the card can show a saving state.
    onSave: (name: string, value: string | null) => Promise<void> | void;
}

const hasValue = (value: unknown): boolean => value !== null && value !== undefined && value !== "";

interface MetadataRow {
    descriptor: MemberMetadataTypeDescriptor;
    stored: string | null;
}

// Staff-only generic Metadata section (Admin + Front-of-House). Lists the
// member's metadata driven entirely by the backend catalogue: editable types get
// an inline add row plus edit/remove; types edited by their own component are
// shown greyed with an "edit elsewhere" tooltip and can't be added or changed
// here. Complex kinds use structured editors (tag list / JSON sub-form) and are
// serialised back to their stored string form on save.
const MetadataCard: React.FC<MetadataCardProps> = ({ member, metadataTypes, onSave }) => {
    const [addType, setAddType] = useState<string | undefined>(undefined);
    const [addValue, setAddValue] = useState<EditorValue>("");
    const [editing, setEditing] = useState<MetadataRow | null>(null);
    const [editValue, setEditValue] = useState<EditorValue>("");
    const [saving, setSaving] = useState(false);

    // Rows: every type the member has a value for, plus every "edited elsewhere"
    // type (always surfaced read-only, even when unset).
    const rows: MetadataRow[] = useMemo(
        () =>
            metadataTypes
                .filter((d) => hasValue(member.metadata?.[d.name]) || !!d.editedInComponent)
                .map((d) => {
                    const raw = member.metadata?.[d.name];
                    return { descriptor: d, stored: hasValue(raw) ? String(raw) : null };
                }),
        [metadataTypes, member.metadata],
    );

    // Addable = editable types not already present.
    const addableTypes = useMemo(
        () =>
            metadataTypes.filter(
                (d) => d.editableInMetadataSection && !hasValue(member.metadata?.[d.name]),
            ),
        [metadataTypes, member.metadata],
    );

    const addDescriptor = addableTypes.find((d) => d.name === addType);

    const persist = async (name: string, value: string | null) => {
        setSaving(true);
        try {
            await onSave(name, value);
        } finally {
            setSaving(false);
        }
    };

    const handleAdd = async () => {
        if (!addDescriptor || !isValidValue(addDescriptor, addValue)) return;
        await persist(addDescriptor.name, serializeValue(addDescriptor, addValue));
        setAddType(undefined);
        setAddValue("");
    };

    const openEdit = (row: MetadataRow) => {
        setEditing(row);
        setEditValue(parseStoredValue(row.descriptor, row.stored));
    };

    const handleEditSave = async () => {
        if (!editing || !isValidValue(editing.descriptor, editValue)) return;
        await persist(editing.descriptor.name, serializeValue(editing.descriptor, editValue));
        setEditing(null);
    };

    const columns = [
        {
            title: "Type",
            dataIndex: "type",
            render: (_: unknown, row: MetadataRow) => (
                <span style={row.descriptor.editedInComponent ? { color: tokens.color.text.disabled } : undefined}>
                    {row.descriptor.label}
                </span>
            ),
        },
        {
            title: "Value",
            dataIndex: "value",
            render: (_: unknown, row: MetadataRow) => {
                const summary = summariseStoredValue(row.descriptor, row.stored);
                if (row.descriptor.editedInComponent) {
                    return (
                        <Tooltip title={`Edit this in the ${row.descriptor.editedInComponent}`}>
                            <span style={{ color: tokens.color.text.disabled }} data-testid={`metadata-elsewhere-${row.descriptor.name}`}>
                                {summary} <InfoCircleOutlined />
                            </span>
                        </Tooltip>
                    );
                }
                return <span>{summary}</span>;
            },
        },
        {
            title: "",
            dataIndex: "actions",
            width: 120,
            render: (_: unknown, row: MetadataRow) => {
                if (row.descriptor.editedInComponent) {
                    return (
                        <Tag bordered={false} color="default" style={{ marginInlineEnd: 0 }}>
                            <LockOutlined style={{ marginInlineEnd: 4 }} />
                            Edit elsewhere
                        </Tag>
                    );
                }
                return (
                    <Space size={tokens.space[1]}>
                        <Button
                            type="link"
                            size="small"
                            icon={<EditOutlined />}
                            onClick={() => openEdit(row)}
                            data-testid={`metadata-edit-${row.descriptor.name}`}
                        />
                        <Popconfirm
                            title="Remove this metadata?"
                            onConfirm={() => persist(row.descriptor.name, null)}
                            okText="Remove"
                            cancelText="Cancel"
                        >
                            <Button
                                type="link"
                                size="small"
                                danger
                                icon={<DeleteOutlined />}
                                data-testid={`metadata-remove-${row.descriptor.name}`}
                            />
                        </Popconfirm>
                    </Space>
                );
            },
        },
    ];

    return (
        <Card
            size="small"
            data-testid="metadata-card"
            title={
                <span style={{ display: "inline-flex", alignItems: "center", gap: tokens.space[2] }}>
                    <DatabaseOutlined style={{ color: tokens.color.text.secondary }} />
                    <span>Metadata</span>
                    <Tag bordered={false} color="gold" style={{ marginInlineStart: 2, marginInlineEnd: 0, fontWeight: tokens.fontWeight.bold }}>
                        <LockOutlined style={{ fontSize: 11, marginInlineEnd: 4 }} />
                        Admin only
                    </Tag>
                </span>
            }
        >
            <Table<MetadataRow>
                rowKey={(row) => row.descriptor.name}
                columns={columns}
                dataSource={rows}
                pagination={false}
                size="small"
                locale={{ emptyText: "No metadata set" }}
                onRow={(row) => ({ "data-testid": `metadata-row-${row.descriptor.name}` } as React.HTMLAttributes<HTMLElement>)}
            />

            <div style={{ display: "flex", gap: tokens.space[2], alignItems: "flex-start", marginTop: tokens.space[3], flexWrap: "wrap" }}>
                <Select
                    value={addType}
                    onChange={(v) => {
                        setAddType(v);
                        const descriptor = addableTypes.find((d) => d.name === v);
                        setAddValue(descriptor?.valueKind === "list" ? [] : descriptor?.valueKind === "json" ? {} : "");
                    }}
                    placeholder="Add metadata…"
                    style={{ minWidth: 200 }}
                    options={addableTypes.map((d) => ({ label: d.label, value: d.name }))}
                    data-testid="metadata-add-type"
                />
                {addDescriptor && (
                    <div style={{ flex: "1 1 220px", minWidth: 220 }}>
                        <MetadataValueEditor descriptor={addDescriptor} value={addValue} onChange={setAddValue} />
                    </div>
                )}
                <Button
                    type="primary"
                    loading={saving}
                    disabled={!addDescriptor || !isValidValue(addDescriptor, addValue)}
                    onClick={handleAdd}
                    data-testid="metadata-add-btn"
                >
                    Add
                </Button>
            </div>

            <Modal
                title={editing ? `Edit ${editing.descriptor.label}` : undefined}
                open={!!editing}
                onCancel={() => (saving ? undefined : setEditing(null))}
                destroyOnClose
                okText="Save"
                okButtonProps={{
                    loading: saving,
                    disabled: !editing || !isValidValue(editing.descriptor, editValue),
                    "data-testid": "metadata-modal-save",
                }}
                onOk={handleEditSave}
            >
                {editing && (
                    <MetadataValueEditor descriptor={editing.descriptor} value={editValue} onChange={setEditValue} />
                )}
            </Modal>
        </Card>
    );
};

export default MetadataCard;
